import { Capacitor } from '@capacitor/core'
import { PricingPhase, PRODUCT_CATEGORY, Purchases, PurchasesPromotionalOffer, PurchasesStoreProduct, PurchasesStoreProductDiscount, PurchasesStoreTransaction, SubscriptionOption } from '@revenuecat/purchases-capacitor'
import UserStore from '@/store/user'
import gradientPioneer from '@/assets/img/gradients/hard/gradient-3.jpg'
import gradientFounder from '@/assets/img/gradients/hard/gradient-8.jpg'
import gradientSupporter from '@/assets/img/gradients/hard/gradient-11.jpg'

export interface Price {
  id: string
  product: Product | undefined
  promotional?: {
    product: PurchasesStoreProductDiscount | null
    offer: PurchasesPromotionalOffer | SubscriptionOption | null
    price: {
      amount: number
      currencyCode: string
    } | null
  } | null
  duration: number
  bg: string
  isDefault?: boolean
}

export interface Product extends PurchasesStoreProduct {
  priceId?: string
}

export const products: Ref<Product[]> = ref([])

export const appleProducts: Ref<{ premium: string[], boost: string[] }> = ref({
  premium: [],
  boost: []
})

export const androidProducts: Ref<{ premium: string[], boost: string[] }> = ref({
  premium: [],
  boost: []
})

export const supporterPrices: Ref<Price[]> = ref([])
export const welcomeOffer: Ref<SubscriptionOption | PurchasesPromotionalOffer | null> = ref(null)
export const welcomeOfferProduct: Ref<PurchasesStoreProductDiscount | null> = ref(null)

export const initIAP = async () => {

  try {
    const userStore = UserStore()
    const { currentUser } = storeToRefs(userStore)

    appleProducts.value = {
      premium: [
        useRuntimeConfig().public.APPLE_PREMIUM_1,
        useRuntimeConfig().public.APPLE_PREMIUM_3,
        useRuntimeConfig().public.APPLE_PREMIUM_12,
        useRuntimeConfig().public.APPLE_PREMIUM_SOLIDARITY,
      ],
      boost: [ 'boost_1', 'boost_5', 'boost_15' ]
    }

    androidProducts.value = {
      premium: [
        useRuntimeConfig().public.ANDROID_PREMIUM_1,
        useRuntimeConfig().public.ANDROID_PREMIUM_3,
        useRuntimeConfig().public.ANDROID_PREMIUM_12,
        useRuntimeConfig().public.ANDROID_PREMIUM_SOLIDARITY,
      ],
      boost: [ 'boost_1', 'boost_5', 'boost_15' ]
    }

    if (!Capacitor.isNativePlatform()) {
      products.value = [
        {
          identifier: 'one-month',
          priceId: useRuntimeConfig().public.STRIPE_MEMBER_0,
          subscriptionPeriod: 'P1M',
          price: 14.99,
          priceString: '14,99€',
          title: '', description: '', introPrice: null,
          currencyCode: '€'
        },
        {
          identifier: '3-months',
          priceId: useRuntimeConfig().public.STRIPE_MEMBER_1,
          subscriptionPeriod: 'P1M',
          discounts: [
            {
              identifier: 'referral',
              price: 14.99,
              priceString: '14,99€',
              cycles: 1,
              period: 'P3M',
              periodUnit: 'MONTH',
              periodNumberOfUnits: 3
            } as PurchasesStoreProductDiscount
          ],
          price: 29.99,
          priceString: '29,99€',
          title: '', description: '', introPrice: null,
          currencyCode: '€'
        },
        {
          identifier: '12-months',
          priceId: useRuntimeConfig().public.STRIPE_MEMBER_2,
          subscriptionPeriod: 'P1M',
          price: 79.99,
          priceString: '79,99€',
          title: '', description: '', introPrice: null,
          currencyCode: '€'
        },
        {
          identifier: 'solidarity',
          priceId: useRuntimeConfig().public.STRIPE_MEMBER_2,
          subscriptionPeriod: 'P1M',
          price: 2.99,
          priceString: '2,99€',
          title: '', description: '', introPrice: null,
          currencyCode: '€'
        },
      ] as any
    } else {
      if (Capacitor.getPlatform() === 'ios') {
        await Purchases.configure({
          apiKey: 'appl_gCKQklUxGyACdfzLEshUHurDfTG',
          appUserID: toRaw(userStore.user?._id)
        })
      } else if (Capacitor.getPlatform() === 'android') {
        await Purchases.configure({
          apiKey: 'goog_wCVQjImxOVWvMArXMSrnsAGfxsE',
          appUserID: toRaw(userStore.user?._id)
        })
      }
      
      watch(() => currentUser.value?._id, () => {
        if (currentUser.value) {
          Purchases.logIn({ appUserID: currentUser.value._id })
        } else {
          Purchases.logOut()
        }
      })

      const ids = Capacitor.getPlatform() === 'ios' ? Object.values(appleProducts.value).reduce((all, products) => [ ...all, ...products], [])  : Object.values(androidProducts.value).reduce((all, products) => [ ...all, ...products], []).map(p => p.split(':')[0])

      const { products: fetchedProducts } = await Purchases.getProducts({
        productIdentifiers: toRaw(ids) as string[],
        type: PRODUCT_CATEGORY.SUBSCRIPTION
      })

      const { products: fetchedConsumables } = await Purchases.getProducts({
        productIdentifiers: toRaw(ids) as string[],
        type: PRODUCT_CATEGORY.NON_SUBSCRIPTION
      })
      
      products.value = [
        ...fetchedProducts,
        ...fetchedConsumables
      ]
    }

    const offer3month = userStore.user?.referral && !(userStore.user?.journey ?? []).includes('is-new-subscriber') ? await offerGet(products.value.find(p => p.identifier.includes('3-months') || p.identifier.includes('3_months')) ?? null, 'referral') : null
    
    supporterPrices.value = [
      {
        id: 'premium-1M',
        product: products.value.find(p => p.identifier.includes('one-month') || p.identifier.includes('1_month')),
        duration: 1,
        bg: gradientSupporter,
      },
      {
        id: 'premium-3M',
        product: products.value.find(p => p.identifier.includes('3-months') || p.identifier.includes('3_months')),
        promotional: offer3month ? {
          product: offer3month.product,
          offer: offer3month.offer,
          price: offer3month.price
        } : null,
        duration: 3,
        bg: gradientPioneer
      },
      {
        id: 'premium-12M',
        product: products.value.find(p => p.identifier.includes('12-months') || p.identifier.includes('12_months')),
        duration: 12,
        bg: gradientFounder
      },
    ].filter(p => p.product)

    if (!offer3month) {
      const result = await offerGet(products.value.find(p => p.identifier.includes('1_month') || p.identifier.includes('1-month') || p.identifier.includes('one-month')) ?? null, 'welcome')

      welcomeOfferProduct.value = result?.product ?? null
      welcomeOffer.value = result?.offer ?? null
    }
  } catch (e) {
    console.error(e)
  }
}

export const purchaseConsumable = async (
  product: PurchasesStoreProduct
) => {
  try {
    const response = await Purchases.purchaseStoreProduct({
      product: toRaw(product),
    })

    return response.productIdentifier === product.identifier || product.identifier.includes(response.productIdentifier) ? response : false
  } catch (e) {
    console.error(e)
    return false
  }
}

export const purchaseProduct = async (
  product: PurchasesStoreProduct,
  options?: {
    discount?: PurchasesPromotionalOffer,
    option?: SubscriptionOption
  }
) => {
  try {
    let response: any = null
    
    if (options?.discount) {
      response = await Purchases.purchaseDiscountedProduct({
        product: toRaw(product),
        discount: toRaw(options.discount)
      })
    } else if (options?.option) {
       response = await Purchases.purchaseSubscriptionOption({
        subscriptionOption: toRaw(options.option)
      })
    } else {
      response = await Purchases.purchaseStoreProduct({
        product: toRaw(product)
      })
    }

    return response && (response.productIdentifier === product.identifier || product.identifier.includes(response.productIdentifier))
  } catch (e) {
    console.error(e)
    return false
  }
}

export const restorePurchases = async () => {
  try {
    const response = await Purchases.restorePurchases()
    return response
  } catch (e) {
    console.error(e)
    return null
  }
}

export const offerGet = async (
  product: Product | null,
  offerId: string
) => {
  const result: {
    product: PurchasesStoreProductDiscount | null,
    offer: PurchasesPromotionalOffer | SubscriptionOption | null,
    price: {
      amount: number
      currencyCode: string
    } | null
  } = {
    product: null,
    offer: null,
    price: null
  }

  if (product === null) return null

  if (Capacitor.getPlatform() === 'ios') {
    result.product = (product.discounts ?? []).find(o => o.identifier === offerId) ?? null

    if (!result.product) return null

    if (result.product) {
      const offer = await Purchases.getPromotionalOffer({
        product: toRaw(product),
        discount: toRaw(result.product)
      })

      if (offer) {
        result.offer = offer

        result.price = {
          amount: result.product.price,
          currencyCode: product.currencyCode
        }
      }
    }
  } else if (Capacitor.getPlatform() === 'android') {
    console.log(JSON.stringify(product))

    result.offer = (product.subscriptionOptions ?? []).find(o => o.id === offerId || o.id.includes(offerId)) ?? null

    if (!result.offer) return null

    result.price = {
      amount: (result.offer?.pricingPhases?.[0]?.price?.amountMicros ?? 0) / 1000000,
      currencyCode: product.currencyCode
    }
  }

  return result
}